import type { ComponentType } from "react";
import { useCallback, useRef, useState } from "react";

import classNames from "classnames";

import type { ScrollArea } from "@similarweb/ui-components";

import useDeactivateOnOutsideClick from "../../../../../hooks/useDeactivateOnOutsideClick";
import type { OpportunityListOption, OpportunityListVariant } from "../../../types";

import ListsDropdownContent from "./ListsDropdownContent/ListsDropdownContent";
import { StyledListsDropdown } from "./styles";
import type { ListsDropdownButtonProps } from "./types";

const DROPDOWN_CLASS_NAME = "opportunity-lists-dropdown";

type OpportunityListsDropdownProps = {
    className?: string;
    renderDropdownButton: ComponentType<ListsDropdownButtonProps>;
    selected: OpportunityListOption;
    groupedLists: Record<OpportunityListVariant, readonly OpportunityListOption[]>;
    onSelect: (list: OpportunityListOption) => void;
};

const OpportunityListsDropdown = ({
    groupedLists,
    selected,
    className,
    renderDropdownButton: DropdownButton,
    onSelect,
}: OpportunityListsDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownScrollAreaRef = useRef<ScrollArea>(null);

    const dropdownClassName = classNames(DROPDOWN_CLASS_NAME, className);

    const toggleDropdown = useCallback(() => setOpen((current) => !current), []);

    const setDropdownScrollAreaRef = useCallback(
        (scrollArea: ScrollArea) => {
            if (dropdownScrollAreaRef) {
                dropdownScrollAreaRef.current = scrollArea;
            }
        },
        [dropdownScrollAreaRef],
    );
    const handleSearch = useCallback(
        (text: string) => {
            if (dropdownScrollAreaRef.current) {
                dropdownScrollAreaRef.current.scrollYTo(0);
            }

            setSearch(text);
        },
        [setSearch],
    );

    useDeactivateOnOutsideClick(dropdownClassName, open, () => setOpen(false));

    return (
        <StyledListsDropdown className={dropdownClassName}>
            <DropdownButton
                list={selected}
                onClick={toggleDropdown}
            />
            <ListsDropdownContent
                open={open}
                groupedLists={groupedLists}
                search={search}
                onSelect={onSelect}
                onSearch={handleSearch}
                selectedList={selected}
                setDropdownScrollAreaRef={setDropdownScrollAreaRef}
            />
        </StyledListsDropdown>
    );
};

export default OpportunityListsDropdown;
